/**
 * Push Notifications — bundled in `app`, so this runs on every page.
 *
 * Two responsibilities:
 *  1. Site-wide repair (every page load): if an existing subscription was
 *     created under a different VAPID key (server-side key rotation), it can no
 *     longer receive pushes — re-subscribe transparently. Only acts when a
 *     subscription already exists, so a user who never opted in is never
 *     prompted or subscribed.
 *  2. Toggle UI (notifications profile page only): the on/off control.
 *
 * VAPID public key is injected as window.__vapidPublicKey (site-wide in
 * base.html.twig).
 *
 * Platform notes:
 *  - Chrome (desktop/Android) and Safari on macOS support Web Push directly.
 *  - On iOS/iPadOS the Push API only exists once the site is installed to the
 *    Home Screen (standalone PWA). In a normal Safari/Chrome tab `PushManager`
 *    is absent, so we detect that case and guide the user to "Add to Home
 *    Screen" instead of showing a dead-end "unavailable" state.
 */
(function () {
    'use strict';

    var vapidKey = window.__vapidPublicKey;
    var hasPush  = 'serviceWorker' in navigator && 'PushManager' in window && !!vapidKey;

    var toggle = document.getElementById('js-push-toggle');
    var label  = document.getElementById('js-push-label');
    var help   = document.getElementById('js-push-help');
    var hasToggleUi = !!(toggle && label);

    // ---- 1. Site-wide repair (runs on every page) ----
    // Re-subscribe when an existing subscription has gone stale. Staleness is
    // decided by shouldResubscribe(): primarily the backend "multiple failed
    // deliveries" signal (browser-agnostic), with the applicationServerKey
    // comparison as a fast local fallback. Permission is already granted for a
    // user who has a subscription, so subscribe() needs no gesture or prompt.
    if (hasPush) {
        navigator.serviceWorker.ready.then(function (reg) {
            return reg.pushManager.getSubscription().then(function (sub) {
                if (!sub) {
                    return null;
                }

                return shouldResubscribe(sub).then(function (stale) {
                    if (!stale) {
                        return sub;
                    }

                    return sub.unsubscribe().then(function () {
                        return doSubscribe(reg);
                    });
                });
            });
        }).then(function (sub) {
            if (hasToggleUi) {
                toggle.checked = !!sub;
                label.textContent = sub ? 'WŁĄCZONE!' : 'WYŁĄCZONE';
            }
        }).catch(function () {
            // Best-effort; leave any UI in its default state on error.
        });
    }

    // ---- 2. Toggle UI (notifications profile page only) ----
    if (!hasToggleUi) return;

    var ua = navigator.userAgent || '';
    var isIOS = /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    var isStandalone = window.navigator.standalone === true ||
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

    // iOS in a normal browser tab: push is only available once installed to the
    // Home Screen. Point the user there rather than showing "unavailable".
    if (!hasPush && isIOS && !isStandalone) {
        disable('NIEDOSTĘPNE');
        showHelp(
            'Aby otrzymywać powiadomienia na iPhonie lub iPadzie, dodaj tę stronę ' +
            'do ekranu początkowego: dotknij <strong>ikony udostępniania</strong> ' +
            'w pasku przeglądarki, wybierz <strong>„Do ekranu początkowego”</strong>, ' +
            'a następnie otwórz stronę z dodanej ikony i włącz powiadomienia tutaj.'
        );
        return;
    }

    if (!hasPush) {
        disable('NIEDOSTĘPNE');
        return;
    }

    toggle.addEventListener('change', function () {
        if (toggle.checked) {
            subscribe();
        } else {
            unsubscribe();
        }
    });

    function showHelp(text) {
        if (!help) return;
        help.innerHTML = text;
        help.hidden = false;
    }

    function disable(labelText) {
        toggle.checked = false;
        toggle.disabled = true;
        label.textContent = labelText;
    }

    function subscribe() {
        // iOS requires requestPermission() to be called synchronously from the
        // user gesture — keep it as the first statement in this handler.
        Notification.requestPermission().then(function (permission) {
            if (permission !== 'granted') {
                toggle.checked = false;
                label.textContent = 'WYŁĄCZONE';
                return;
            }

            navigator.serviceWorker.ready.then(function (reg) {
                return doSubscribe(reg);
            }).then(function () {
                label.textContent = 'WŁĄCZONE!';
            }).catch(function () {
                toggle.checked = false;
                label.textContent = 'WYŁĄCZONE';
            });
        });
    }

    function unsubscribe() {
        navigator.serviceWorker.ready.then(function (reg) {
            return reg.pushManager.getSubscription();
        }).then(function (sub) {
            if (sub) return sub.unsubscribe();
        }).then(function () {
            return fetch('/profile/push-unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
        }).then(function () {
            label.textContent = 'WYŁĄCZONE';
        }).catch(function () {
            label.textContent = 'WYŁĄCZONE';
        });
    }

    // ---- Shared helpers (no DOM dependency; safe site-wide) ----

    // Subscribe with the current VAPID key and persist the subscription server-side.
    function doSubscribe(reg) {
        return reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey)
        }).then(function (sub) {
            return fetch('/profile/push-subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sub.toJSON())
            }).then(function () {
                return sub;
            });
        });
    }

    // Decide whether an existing subscription should be replaced.
    // Fallback (fast, local): if the browser exposes the subscription's key and
    // it differs from what we serve, that's a definite mismatch — re-subscribe
    // without asking the server.
    // Primary (browser-agnostic): otherwise ask the backend whether this
    // subscription has failed delivery multiple times. Works even when the key
    // is not readable from the subscription (e.g. Safari). Consulted at most
    // once per session to avoid a request on every page view; the backend
    // baselines failures at the last subscription change, so a fresh
    // subscription reports zero and this can't loop.
    function shouldResubscribe(sub) {
        if (subscriptionKeyDiffers(sub)) {
            return Promise.resolve(true);
        }

        try {
            if (sessionStorage.getItem('pushStatusChecked')) {
                return Promise.resolve(false);
            }
        } catch (e) { /* sessionStorage unavailable — just query */ }

        return fetch('/profile/push-subscription-status', {
            headers: { 'Accept': 'application/json' }
        }).then(function (r) {
            return r.ok ? r.json() : null;
        }).then(function (data) {
            try { sessionStorage.setItem('pushStatusChecked', '1'); } catch (e) {}
            return !!(data && data.shouldResubscribe);
        }).catch(function () {
            return false;
        });
    }

    // True only if the subscription's key is readable AND differs from the
    // current one. If the key can't be read, returns false so the decision
    // falls through to the backend signal.
    function subscriptionKeyDiffers(sub) {
        var existing = sub.options && sub.options.applicationServerKey;
        if (!existing) return false;

        var current = urlBase64ToUint8Array(vapidKey);
        var actual  = new Uint8Array(existing);
        if (actual.length !== current.length) return true;
        for (var i = 0; i < actual.length; i++) {
            if (actual[i] !== current[i]) return true;
        }
        return false;
    }

    function urlBase64ToUint8Array(base64String) {
        var padding = '='.repeat((4 - base64String.length % 4) % 4);
        var base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        var raw = atob(base64);
        var arr = new Uint8Array(raw.length);
        for (var i = 0; i < raw.length; i++) {
            arr[i] = raw.charCodeAt(i);
        }
        return arr;
    }
})();
